import {
  GetSpendingControlsV2QuerySchema,
  GetSpendingControlsV2ResponseSchema,
  type SpendingControlControlType,
} from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { useGetParsedQuery } from "@src/appV2/api/useGetParsedQuery";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";

interface UseGetSpendingControlsParams {
  workplaceId: string;
  weekStartDate: Date;
  /** Server-side filter; a single type or a list. The v2 endpoint accepts both. */
  controlType?: SpendingControlControlType | readonly SpendingControlControlType[];
}

interface UseGetSpendingControlsOptions {
  enabled?: boolean;
  /** Cache window; defaults to {@link SPENDING_CONTROLS_STALE_TIME_MS}. Pair `Infinity` with `refetchOnMount: "always"` for fresh-on-mount, no auto-refetch. */
  staleTime?: number;
  refetchOnMount?: boolean | "always";
}

const I18N_SKIP_API_VERSION = "v2";
const I18N_SKIP_WORKPLACES_PATH = "workplaces";
const I18N_SKIP_SPENDING_CONTROLS_PATH = "spending-controls";
const I18N_SKIP_USAGE_INCLUDE = "usage";

const SPENDING_CONTROLS_STALE_TIME_MS = 60_000;

/**
 * Reads spending controls from the v2 endpoint with the `usage` include; pair
 * with {@link joinSpendingControlsWithUsage} to flatten the response before use.
 */
export function useGetSpendingControls(
  params: UseGetSpendingControlsParams,
  options: UseGetSpendingControlsOptions = {}
) {
  const { workplaceId, weekStartDate, controlType } = params;
  const { enabled = true, staleTime = SPENDING_CONTROLS_STALE_TIME_MS, refetchOnMount } = options;

  const url = `${environmentConfig.REACT_APP_BASE_API_URL}/${I18N_SKIP_API_VERSION}/${I18N_SKIP_WORKPLACES_PATH}/${workplaceId}/${I18N_SKIP_SPENDING_CONTROLS_PATH}`;

  return useGetParsedQuery({
    url,
    queryParams: {
      filter: {
        weekStartDate,
        ...(isDefined(controlType) ? { controlType } : {}),
      },
      // Array form serializes to `?include=usage` via parsedApi's comma format.
      include: [I18N_SKIP_USAGE_INCLUDE],
    },
    queryParamsSchema: GetSpendingControlsV2QuerySchema,
    responseSchema: GetSpendingControlsV2ResponseSchema,
    meta: { logErrorMessage: APP_V2_APP_EVENTS.GET_SPENDING_CONTROLS_FAILURE },
    enabled: enabled && isDefined(workplaceId),
    staleTime,
    refetchOnMount,
  });
}
